<script lang="ts">
import { onMount } from "svelte";
import {
  GeolocateControl,
  LngLat as MapLibreLngLat,
  LngLatBounds,
  Marker,
} from "maplibre-gl";
import type { Map } from "maplibre-gl";
import { CustomControl } from "svelte-maplibre-gl";

import type { LngLat } from "../lib/geo/types.ts";

type GeolocationStatus = "active" | "inactive";

let {
  map,
  onlocationchange,
  ongeolocationstatuschange,
}: {
  map: Map | undefined;
  onlocationchange?: (lngLat: LngLat | undefined) => void;
  // Active means the user requested browser geolocation or passive tracking
  // started because the browser already granted permission. Inactive means
  // the permission check completed without browser geolocation being active.
  ongeolocationstatuschange?: (status: GeolocationStatus) => void;
} = $props();

// MapLibre 6 emits its own event class, while the browser API uses
// GeolocationPosition. Both provide the coordinates needed by this control.
type UserPosition = Pick<GeolocationPosition, "coords">;

// Keep the passive startup marker app-owned and disable MapLibre's duplicate
// marker. See repo-root docs/adr/009-passive-geolocation-marker.md.
const geolocateControl = new GeolocateControl({
  positionOptions: {
    enableHighAccuracy: true,
    maximumAge: 0,
    timeout: 6000,
  },
  trackUserLocation: false,
  showUserLocation: false,
});
const nativeGeolocateTrigger = geolocateControl.trigger.bind(
  geolocateControl,
);

let geolocationWatchId: number | undefined;
let userLocationMarker: Marker | undefined;
let lastUserPosition = $state<UserPosition | undefined>(undefined);

geolocateControl.trigger = () => {
  ongeolocationstatuschange?.("active");
  if (!map || !lastUserPosition) return nativeGeolocateTrigger();

  centerMapOnUserPosition(lastUserPosition);
  startUserLocationTracking();
  return true;
};

geolocateControl.on("geolocate", (position: UserPosition) => {
  updateUserLocationMarker(position);
  startUserLocationTracking();
});

function renderUserLocationMarker(): void {
  if (!map || !lastUserPosition) return;

  const { latitude, longitude } = lastUserPosition.coords;

  if (!userLocationMarker) {
    const dot = document.createElement("div");
    dot.className = "maplibregl-user-location-dot";
    userLocationMarker = new Marker({
      element: dot,
      anchor: "center",
    });
  }

  userLocationMarker.setLngLat([longitude, latitude]).addTo(map);
}

function updateUserLocationMarker(position: UserPosition): void {
  lastUserPosition = position;
  onlocationchange?.([position.coords.longitude, position.coords.latitude]);
  renderUserLocationMarker();
}

function centerMapOnUserPosition(position: UserPosition): void {
  if (!map) return;

  const { accuracy, latitude, longitude } = position.coords;
  const center = new MapLibreLngLat(longitude, latitude);
  map.fitBounds(
    LngLatBounds.fromLngLat(center, accuracy),
    { bearing: map.getBearing(), maxZoom: 15 },
  );
}

function startUserLocationTracking(): void {
  if (geolocationWatchId !== undefined || !navigator.geolocation) {
    return;
  }

  geolocationWatchId = navigator.geolocation.watchPosition(
    updateUserLocationMarker,
    () => {
      // Intentionally ignore passive location errors.
    },
    { enableHighAccuracy: true, maximumAge: 0, timeout: 6000 },
  );
  ongeolocationstatuschange?.("active");
}

function stopUserLocationTracking(): void {
  if (geolocationWatchId !== undefined) {
    navigator.geolocation.clearWatch(geolocationWatchId);
    geolocationWatchId = undefined;
  }

  userLocationMarker?.remove();
  userLocationMarker = undefined;
  lastUserPosition = undefined;
  onlocationchange?.(undefined);
}

$effect(() => {
  renderUserLocationMarker();
});

onMount(() => {
  const permissionQuery = navigator.permissions?.query({ name: "geolocation" });
  if (!permissionQuery) {
    ongeolocationstatuschange?.("inactive");
  } else {
    void permissionQuery
      .then((permission) => {
        if (permission.state === "granted") {
          startUserLocationTracking();
        } else {
          ongeolocationstatuschange?.("inactive");
        }
      })
      .catch(() => {
        ongeolocationstatuschange?.("inactive");
      });
  }

  return stopUserLocationTracking;
});
</script>

<CustomControl position="top-right" control={geolocateControl} />
